import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  * { box-sizing: border-box; }

  .dash-root {
    display: flex;
    min-height: 100vh;
    background: #0a0a0f;
    font-family: 'DM Sans', sans-serif;
    color: white;
  }

  /* ── Sidebar ── */
  .dash-sidebar {
    width: 220px;
    min-height: 100vh;
    background: rgba(255,255,255,0.02);
    border-right: 1px solid rgba(255,255,255,0.06);
    display: flex;
    flex-direction: column;
    padding: 28px 16px;
    flex-shrink: 0;
  }

  .dash-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 8px;
    margin-bottom: 36px;
  }

  .dash-brand-icon {
    width: 34px;
    height: 34px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 12px;
    color: white;
  }

  .dash-brand-text {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: rgba(255,255,255,0.8);
    letter-spacing: 2px;
  }

  .dash-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .dash-nav-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: none;
    border: none;
    color: rgba(255,255,255,0.4);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
    width: 100%;
  }

  .dash-nav-btn:hover {
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.7);
  }

  .dash-nav-btn.active {
    background: rgba(99,102,241,0.15);
    color: #a5b4fc;
    font-weight: 500;
  }

  .dash-nav-icon {
    width: 18px;
    height: 18px;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .dash-logout {
    border-top: 1px solid rgba(255,255,255,0.06);
    padding-top: 16px;
    margin-top: 8px;
    color: rgba(239,68,68,0.6) !important;
  }

  .dash-logout:hover {
    color: rgba(239,68,68,0.9) !important;
    background: rgba(239,68,68,0.08) !important;
  }

  /* ── Main content ── */
  .dash-main {
    flex: 1;
    padding: 36px 40px;
    overflow-y: auto;
  }

  .dash-page-header {
    margin-bottom: 32px;
  }

  .dash-page-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 26px;
    color: white;
    margin: 0 0 6px 0;
  }

  .dash-page-sub {
    font-size: 14px;
    color: rgba(255,255,255,0.35);
    margin: 0;
  }

  /* ── Cards ── */
  .dash-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 16px;
    padding: 24px;
  }

  .dash-card-title {
    font-family: 'Syne', sans-serif;
    font-weight: 600;
    font-size: 15px;
    color: rgba(255,255,255,0.7);
    margin: 0 0 20px 0;
    letter-spacing: 0.3px;
  }

  /* ── Stats grid ── */
  .dash-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }

  .dash-stat {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 18px 20px;
  }

  .dash-stat-label {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    margin-bottom: 8px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .dash-stat-value {
    font-family: 'Syne', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: white;
  }

  .dash-stat-value.purple { color: #a5b4fc; }
  .dash-stat-value.pink { color: #f9a8d4; }

  /* ── Connect button ── */
  .dash-btn-connect {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 13px 24px;
    background: linear-gradient(135deg, #e1306c, #833ab4, #405de6);
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s;
  }

  .dash-btn-connect:hover { opacity: 0.9; transform: translateY(-1px); }
  .dash-btn-connect:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* ── Connected badge ── */
  .dash-badge-connected {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: rgba(16,185,129,0.1);
    border: 1px solid rgba(16,185,129,0.2);
    border-radius: 20px;
    font-size: 13px;
    color: #34d399;
  }

  .dash-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #34d399;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  /* ── Instagram Profile Card ── */
  .insta-profile-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .insta-avatar-wrap {
    position: relative;
    flex-shrink: 0;
  }

  .insta-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }

  .insta-avatar-placeholder {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e1306c, #833ab4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 24px;
    color: white;
  }

  .insta-ring {
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e1306c, #833ab4, #405de6);
    z-index: -1;
  }

  .insta-username {
    font-family: 'Syne', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: white;
    margin: 0 0 4px 0;
  }

  .insta-name {
    font-size: 14px;
    color: rgba(255,255,255,0.45);
    margin: 0 0 8px 0;
  }

  .insta-bio {
    font-size: 13px;
    color: rgba(255,255,255,0.55);
    line-height: 1.5;
    margin: 0;
  }

  .insta-website {
    font-size: 13px;
    color: #a5b4fc;
    text-decoration: none;
    display: block;
    margin-top: 6px;
  }

  /* ── Posts grid ── */
  .insta-posts-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    border-radius: 12px;
    overflow: hidden;
  }

  .insta-post-thumb {
    aspect-ratio: 1;
    background: rgba(255,255,255,0.05);
    position: relative;
    overflow: hidden;
  }

  .insta-post-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.2s;
  }

  .insta-post-thumb:hover img { transform: scale(1.05); }

  .insta-post-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    font-size: 13px;
    color: white;
    font-weight: 500;
    gap: 12px;
  }

  .insta-post-thumb:hover .insta-post-overlay { opacity: 1; }

  .insta-post-skeleton {
    aspect-ratio: 1;
    background: rgba(255,255,255,0.05);
    border-radius: 4px;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% { background: rgba(255,255,255,0.04); }
    50% { background: rgba(255,255,255,0.08); }
    100% { background: rgba(255,255,255,0.04); }
  }

  /* ── Not connected state ── */
  .dash-empty {
    text-align: center;
    padding: 48px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .dash-empty-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: rgba(255,255,255,0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .dash-empty-text {
    font-size: 14px;
    color: rgba(255,255,255,0.35);
    margin: 0;
    max-width: 280px;
    line-height: 1.6;
  }

  /* ── Layout grids ── */
  .dash-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .dash-grid-full {
    margin-bottom: 16px;
  }

  /* ── Loading spinner ── */
  .dash-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
    color: rgba(255,255,255,0.3);
    font-size: 14px;
    gap: 12px;
  }

  .spin {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255,255,255,0.1);
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Profile page ── */
  .dash-profile-wrap {
    max-width: 560px;
  }

  .dash-user-info {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 16px;
    margin-bottom: 16px;
  }

  .dash-user-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 18px;
    color: white;
    flex-shrink: 0;
  }

  .dash-user-name {
    font-family: 'Syne', sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: white;
    margin: 0 0 4px 0;
  }

  .dash-user-email {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    margin: 0;
  }

  /* ── Messages ── */
  .dash-messages-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    gap: 16px;
    text-align: center;
  }

  .dash-messages-icon {
    font-size: 40px;
    opacity: 0.3;
  }

  .dash-messages-text {
    font-size: 14px;
    color: rgba(255,255,255,0.3);
    margin: 0;
  }
`;

// ── Icon components ──
const HomeIcon = () => (
  <svg className="dash-nav-icon" viewBox="0 0 20 20" fill="currentColor">
    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
  </svg>
);
const ProfileIcon = () => (
  <svg className="dash-nav-icon" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
  </svg>
);
const MsgIcon = () => (
  <svg className="dash-nav-icon" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
  </svg>
);
const LogoutIcon = () => (
  <svg className="dash-nav-icon" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"/>
  </svg>
);
const InstaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// ── Dashboard Component ──
const Dashboard = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState('feed');
  const [loading, setLoading] = useState(false);
  const [instaLoading, setInstaLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);

  const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');
  const user = getUser();

  // isConnected = user already has instagram connected (stored in DB, comes via login)
  const [isConnected, setIsConnected] = useState(!!(user?.instagram?.accessToken));

  // On mount: protect route + auto-fetch if already connected
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    const u = getUser();
    if (u?.instagram?.accessToken) {
      setIsConnected(true);
      fetchInstagramProfile();
    }
  }, []);

  // Handle Instagram OAuth redirect (code in URL)
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const code = query.get('code');
    if (code) {
      exchangeCodeForToken(code);
    }
  }, []);

  // Fetch Instagram profile + media from backend
  const fetchInstagramProfile = async () => {
    try {
      setInstaLoading(true);
      const token = localStorage.getItem('token');

      const res = await axios.get('https://dev-socialapp-api.workforce.asquarei.ai/api/instagram/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;
      // Backend returns { profile: {...}, media: { data: [...] } }
      setProfile(data.profile || null);
      setPosts(data.media?.data || []);
    } catch (err) {
      console.error('Instagram profile fetch error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('insta_token');
        setIsConnected(false);
        setProfile(null);
        setPosts([]);
      }
    } finally {
      setInstaLoading(false);
    }
  };

  const handleConnectInstagram = async () => {
    try {
      setLoading(true);
      const res = await axios.get('https://dev-socialapp-api.workforce.asquarei.ai/api/instagram/auth-url');
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Auth URL error:', err);
      setLoading(false);
    }
  };

  const exchangeCodeForToken = async (code) => {
    try {
      setLoading(true);
      const jwtToken = localStorage.getItem('token');
      // Backend saves instagram token to DB and returns updated user
      const res = await axios.post(
        'https://dev-socialapp-api.workforce.asquarei.ai/api/instagram/token',
        { code },
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );
      // Update user in localStorage with new instagram data
      if (res.data.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }
      setIsConnected(true);
      fetchInstagramProfile();
      window.history.replaceState({}, document.title, '/dashboard');
    } catch (err) {
      console.error('Token exchange error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const jwtToken = localStorage.getItem('token');
      await axios.post(
        'https://dev-socialapp-api.workforce.asquarei.ai/api/instagram/disconnect',
        {},
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );
    } catch (err) {
      console.error('Disconnect error:', err);
    }
    // Remove instagram from local user object
    const u = getUser();
    if (u?.instagram) {
      delete u.instagram;
      localStorage.setItem('user', JSON.stringify(u));
    }
    setIsConnected(false);
    setProfile(null);
    setPosts([]);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'AI';
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '--';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <>
      <style>{styles}</style>
      <div className="dash-root">

        {/* ── Sidebar ── */}
        <aside className="dash-sidebar">
          <div className="dash-brand">
            <img
              src="/signinsignuplogo.png"
              alt="Ai Workforce"
              style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
            />
          </div>

          <nav className="dash-nav">
            <button
              className={`dash-nav-btn ${active === 'feed' ? 'active' : ''}`}
              onClick={() => setActive('feed')}
            >
              <HomeIcon /> Dashboard
            </button>
            <button
              className={`dash-nav-btn ${active === 'profile' ? 'active' : ''}`}
              onClick={() => setActive('profile')}
            >
              <ProfileIcon /> Profile
            </button>
            <button
              className={`dash-nav-btn ${active === 'messages' ? 'active' : ''}`}
              onClick={() => setActive('messages')}
            >
              <MsgIcon /> Messages
            </button>
          </nav>

          <button className="dash-nav-btn dash-logout" onClick={handleLogout}>
            <LogoutIcon /> Logout
          </button>
        </aside>

        {/* ── Main ── */}
        <main className="dash-main">

          {/* ── FEED TAB ── */}
          {active === 'feed' && (
            <>
              <div className="dash-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h1 className="dash-page-title">Dashboard</h1>
                  <p className="dash-page-sub">Welcome back, {user.firstName || 'User'} 👋</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {isConnected ? (
                    <>
                      <span className="dash-badge-connected">
                        <span className="dash-dot" /> Instagram Connected
                      </span>
                      <button
                        onClick={handleDisconnect}
                        style={{
                          background: 'rgba(239,68,68,0.1)',
                          border: '1px solid rgba(239,68,68,0.2)',
                          borderRadius: 10,
                          color: '#f87171',
                          fontSize: 13,
                          padding: '8px 14px',
                          cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      className="dash-btn-connect"
                      onClick={handleConnectInstagram}
                      disabled={loading}
                    >
                      <InstaIcon />
                      {loading ? 'Connecting...' : 'Connect Instagram'}
                    </button>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="dash-stats-grid">
                <div className="dash-stat">
                  <div className="dash-stat-label">Account Type</div>
                  <div className="dash-stat-value purple" style={{ fontSize: 16, paddingTop: 4 }}>
                    {isConnected && profile ? profile.account_type || '--' : '--'}
                  </div>
                </div>
                <div className="dash-stat">
                  <div className="dash-stat-label">Total Posts</div>
                  <div className="dash-stat-value">
                    {isConnected && profile ? formatNumber(profile.media_count) : '--'}
                  </div>
                </div>
                <div className="dash-stat">
                  <div className="dash-stat-label">Username</div>
                  <div className="dash-stat-value pink" style={{ fontSize: 16, paddingTop: 4 }}>
                    {isConnected && profile ? `@${profile.username}` : '--'}
                  </div>
                </div>
              </div>

              {/* Instagram Profile Card */}
              <div className="dash-grid-full">
                <div className="dash-card">
                  <p className="dash-card-title">Instagram Account</p>

                  {!isConnected ? (
                    <div className="dash-empty">
                      <div className="dash-empty-icon">📷</div>
                      <p className="dash-empty-text">
                        Connect your Instagram to see your profile, posts, and analytics here.
                      </p>
                      <button className="dash-btn-connect" onClick={handleConnectInstagram} disabled={loading}>
                        <InstaIcon />
                        {loading ? 'Connecting...' : 'Connect Instagram'}
                      </button>
                    </div>
                  ) : instaLoading ? (
                    <div className="dash-loading">
                      <div className="spin" /> Loading Instagram data...
                    </div>
                  ) : profile ? (
                    <div className="insta-profile-header">
                      <div className="insta-avatar-wrap">
                        <div className="insta-ring" />
                        {profile.profile_picture_url ? (
                          <img className="insta-avatar" src={profile.profile_picture_url} alt="profile" />
                        ) : (
                          <div className="insta-avatar-placeholder">
                            {profile.username?.[0]?.toUpperCase() || 'I'}
                          </div>
                        )}
                      </div>
                      <div>
                        <h2 className="insta-username">@{profile.username}</h2>
                        {profile.name && <p className="insta-name">{profile.name}</p>}
                        <p className="insta-name" style={{ marginBottom: 8 }}>
                          {profile.account_type} Account &nbsp;·&nbsp; {profile.media_count} posts
                        </p>
                        {profile.biography && <p className="insta-bio">{profile.biography}</p>}
                        {profile.website && (
                          <a className="insta-website" href={profile.website} target="_blank" rel="noreferrer">
                            {profile.website}
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Could not load profile data.</p>
                  )}
                </div>
              </div>

              {/* Posts Grid */}
              {isConnected && (
                <div className="dash-grid-full">
                  <div className="dash-card">
                    <p className="dash-card-title">Recent Posts</p>
                    {instaLoading ? (
                      <div className="insta-posts-grid">
                        {[...Array(9)].map((_, i) => (
                          <div key={i} className="insta-post-skeleton" />
                        ))}
                      </div>
                    ) : posts.length > 0 ? (
                      <div className="insta-posts-grid">
                        {posts.map((post) => (
                          <a
                            key={post.id}
                            href={post.permalink}
                            target="_blank"
                            rel="noreferrer"
                            className="insta-post-thumb"
                            style={{ textDecoration: 'none', display: 'block' }}
                          >
                            {(post.thumbnail_url || post.media_url) ? (
                              <>
                                <img
                                  src={post.thumbnail_url || post.media_url}
                                  alt="post"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <div className="insta-post-overlay">
                                  {post.media_type === 'VIDEO' ? '🎬 Reel' : '🖼️ View'}
                                </div>
                              </>
                            ) : (
                              <div style={{
                                width: '100%', height: '100%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(255,255,255,0.05)', fontSize: 20,
                              }}>
                                {post.media_type === 'VIDEO' ? '🎬' : '🖼️'}
                              </div>
                            )}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="dash-empty">
                        <p className="dash-empty-text">No posts found on this account.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── PROFILE TAB ── */}
          {active === 'profile' && (
            <>
              <div className="dash-page-header">
                <h1 className="dash-page-title">Profile</h1>
                <p className="dash-page-sub">Your account information</p>
              </div>

              <div className="dash-profile-wrap">
                <div className="dash-user-info">
                  <div className="dash-user-avatar">
                    {getInitials(user.firstName, user.lastName)}
                  </div>
                  <div>
                    <p className="dash-user-name">{user.firstName} {user.lastName}</p>
                    <p className="dash-user-email">{user.email}</p>
                  </div>
                </div>

                {/* Account details card */}
                <div className="dash-card" style={{ marginBottom: 16 }}>
                  <p className="dash-card-title">Account Details</p>
                  <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
                    {[
                      ['First Name', user.firstName || '--'],
                      ['Last Name', user.lastName || '--'],
                      ['Email', user.email || '--'],
                      ['Phone', user.phone || '--'],
                    ].map(([label, val]) => (
                      <tr key={label} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '12px 0', color: 'rgba(255,255,255,0.35)', width: 140 }}>{label}</td>
                        <td style={{ padding: '12px 0', color: 'rgba(255,255,255,0.75)' }}>{val}</td>
                      </tr>
                    ))}
                  </table>
                </div>

                {/* Instagram Status */}
                <div className="dash-card">
                  <p className="dash-card-title">Connected Accounts</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: 'linear-gradient(135deg, #e1306c, #833ab4, #405de6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <InstaIcon />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 14, color: 'white', fontWeight: 500 }}>Instagram</p>
                        {isConnected && profile ? (
                          <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>@{profile.username}</p>
                        ) : (
                          <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Not connected</p>
                        )}
                      </div>
                    </div>
                    {isConnected ? (
                      <button
                        onClick={handleDisconnect}
                        style={{
                          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                          borderRadius: 8, color: '#f87171', fontSize: 13,
                          padding: '7px 14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button className="dash-btn-connect" style={{ padding: '9px 16px', fontSize: 13 }} onClick={handleConnectInstagram}>
                        <InstaIcon /> Connect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── MESSAGES TAB ── */}
          {active === 'messages' && (
            <>
              <div className="dash-page-header">
                <h1 className="dash-page-title">Messages</h1>
                <p className="dash-page-sub">Your Instagram DMs will appear here</p>
              </div>

              <div className="dash-card">
                {!isConnected ? (
                  <div className="dash-empty">
                    <div className="dash-empty-icon">💬</div>
                    <p className="dash-empty-text">Connect Instagram to view and manage your direct messages.</p>
                    <button className="dash-btn-connect" onClick={handleConnectInstagram} disabled={loading}>
                      <InstaIcon /> {loading ? 'Connecting...' : 'Connect Instagram'}
                    </button>
                  </div>
                ) : (
                  <div className="dash-messages-empty">
                    <div className="dash-messages-icon">🚀</div>
                    <p className="dash-messages-text">Messages integration coming soon. Your DMs will show up here once the API is connected.</p>
                  </div>
                )}
              </div>
            </>
          )}

        </main>
      </div>
    </>
  );
};

export default Dashboard;
